import { useMemo } from "react";
import { useParams, Link, useLocation, useSearch } from "wouter";
import { useResellers } from "@/hooks/use-inventory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, AlertTriangle, PackageSearch } from "lucide-react";
import { ResellerIdentitySection, SitesSection, ResellerQRSection, useCanEditReseller, RobotsSection, PuduRobotsSection, ExtensionsGarantieSection, ResellerTicketsSection, CommandesResellerSection, AdherentsSection, RelationSection, TarificationSection, ContratsSection, PerformanceSection, PortailAccessSection } from "./sections";
import { ResellerFileExplorer } from "./FileExplorer";
import { EntiteIntervenants } from "@/components/ContactLiens";
import { ActionsLiees } from "@/components/actions-liees";
import type { Reseller } from "@shared/schema";

const TAB_VALUES = ["general", "partenariat", "parc", "contacts", "documents", "activite"] as const;
type TabValue = typeof TAB_VALUES[number];

export default function ResellerDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const { data: resellersList, isLoading, isError } = useResellers();
  const canEdit = useCanEditReseller();

  // ── Onglet actif depuis URL — fallback silencieux sur "general" ────────────
  const rawTab = new URLSearchParams(search).get("tab") ?? "";
  const activeTab: TabValue = (TAB_VALUES as readonly string[]).includes(rawTab)
    ? (rawTab as TabValue)
    : "general";

  function setActiveTab(tab: string) {
    navigate(`/resellers/${id}?tab=${tab}`, { replace: true });
  }

  const reseller = useMemo(
    () => (resellersList ?? []).find((r: Reseller) => r.id === id) ?? null,
    [resellersList, id],
  );

  // ── État loading ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="h-[120px] w-full rounded-xl" />
        <div className="flex gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-24 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-[300px] w-full rounded-xl" />
      </div>
    );
  }

  // ── État erreur réseau ────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-destructive" />
        </div>
        <div className="text-center">
          <p className="font-semibold text-foreground">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">
            Impossible de récupérer la liste des revendeurs. Vérifiez votre connexion.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link href="/resellers">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la liste
          </Link>
        </Button>
      </div>
    );
  }

  // ── État not-found ────────────────────────────────────────────────────────
  if (!reseller) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <PackageSearch className="w-6 h-6 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="font-semibold text-foreground">Revendeur introuvable</p>
          <p className="text-sm text-muted-foreground mt-1">
            Aucun revendeur ne correspond à cet identifiant.
          </p>
        </div>
        <Button variant="outline" asChild data-testid="link-back-resellers">
          <Link href="/resellers">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la liste
          </Link>
        </Button>
      </div>
    );
  }

  // ── Fiche principale ──────────────────────────────────────────────────────
  const displayName = (reseller as any).nomCommercial || reseller.companyName;

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6 max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link
          href={(() => { try { return '/resellers' + (sessionStorage.getItem('resellers-list-qs') || ''); } catch { return '/resellers'; } })()}
          className="hover:text-foreground transition-colors flex items-center gap-1"
          data-testid="link-breadcrumb-resellers"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Revendeurs
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{displayName}</span>
      </div>

      {/* Onglets — contrôlés par l'URL */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
        data-testid="tabs-reseller-detail"
      >
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="general" data-testid="tab-general">Général</TabsTrigger>
          <TabsTrigger value="partenariat" data-testid="tab-partenariat">Partenariat</TabsTrigger>
          <TabsTrigger value="parc" data-testid="tab-parc">Parc</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="activite" data-testid="tab-activite">Activité / SAV</TabsTrigger>
        </TabsList>

        {/* ── Onglet Général ─────────────────────────────────────────────── */}
        <TabsContent value="general" className="space-y-4 text-sm" data-testid="tabcontent-general">
          <ResellerIdentitySection
            reseller={reseller}
            canEdit={canEdit}
          />
          <SitesSection resellerId={reseller.id} canEdit={canEdit} />
          <ResellerQRSection />
        </TabsContent>

        {/* ── Onglet Partenariat ──────────────────────────────────────────── */}
        <TabsContent value="partenariat" data-testid="tabcontent-partenariat">
          {activeTab === "partenariat" && (
            <div className="space-y-2 text-sm pt-1">
              <AdherentsSection reseller={reseller} onSelectReseller={(id) => navigate(`/resellers/${id}`)} />
              <RelationSection reseller={reseller} canEdit={canEdit} />
              <TarificationSection reseller={reseller} />
              <ContratsSection resellerId={reseller.id} canEdit={canEdit} />
              <PerformanceSection resellerId={reseller.id} />
              <PortailAccessSection resellerId={reseller.id} canEdit={canEdit} />
            </div>
          )}
        </TabsContent>

        {/* ── Onglet Parc — lazy sur l'ensemble (Pudu = API externe) ─────── */}
        <TabsContent value="parc" data-testid="tabcontent-parc">
          {activeTab === "parc" && (
            <div className="space-y-2">
              <RobotsSection resellerId={reseller.id} canEdit={canEdit} />
              <PuduRobotsSection resellerId={reseller.id} />
              <ExtensionsGarantieSection />
            </div>
          )}
        </TabsContent>

        {/* ── Onglet Contacts — lazy (query part au montage, guard obligatoire) */}
        <TabsContent value="contacts" data-testid="tabcontent-contacts">
          {activeTab === "contacts" && (
            <EntiteIntervenants entiteType="revendeur" entiteId={reseller.id} />
          )}
        </TabsContent>

        {/* ── Onglet Documents — lazy (Radix garde le panel monté via hidden) */}
        <TabsContent value="documents" data-testid="tabcontent-documents">
          {activeTab === "documents" && (
            <ResellerFileExplorer resellerId={reseller.id} canEdit={canEdit} />
          )}
        </TabsContent>

        {/* ── Onglet Activité / SAV — lazy (3 queries ne partent qu'à l'ouverture) */}
        <TabsContent value="activite" data-testid="tabcontent-activite">
          {activeTab === "activite" && (
            <div className="space-y-2">
              <ResellerTicketsSection resellerId={reseller.id} />
              <ActionsLiees emetteurType="revendeur" emetteurId={reseller.id} />
              <CommandesResellerSection resellerId={reseller.id} />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
